// The store arrives as a parameter; its type lives in `shared/lib/preferenceStore`, moved there on
// 17 September 2026 when the navigation panel became a third preference of the same shape and a
// reader in `app/`, which cannot import from `pages/`. Nothing about the contract changed.
import type { DayDockMode } from '~/features/plan-day-programming';
import type { PreferenceStore } from '~/shared/lib/preferenceStore';

export type { DayDockMode };

/**
 * Which host a day OPENS in, remembered between visits.
 *
 * ── The URL owns which day; this owns which BOX, and only as a default ──────────────────────
 * `usePlanBuilderSearch`'s header states the division and this is exactly the category it reserves:
 * localStorage holds defaults — density, a collapsed rail — never a selection. `?day=&dock=` is
 * still the whole of what is open right now, so a link still works, Back still collapses the dock,
 * and nothing here can disagree with the address. What is stored is only the answer to "when the
 * trainer presses a day card, which host should it appear in", which is a preference about this
 * trainer's way of working rather than a fact about this plan.
 *
 * The owner's requirement is that the choice STICKS: press maximise once and every following day
 * opens in the modal, until the restore in the modal's own corner sends them back to the panel.
 * One value, written by that toggle and read by `open`.
 *
 * ── The STORE is a parameter, and that is what keeps this module in `model/` ────────────────
 * It reached for `window.localStorage` itself until 13 September 2026, which made it the one file
 * in this segment that could not run without a DOM and made its test redefine a property on
 * `window` to say anything at all. The two methods it needs arrive instead, the same way
 * `entities/session/model/resilientStorage.ts` takes its backing store — so the decision here is
 * "what does a stored string mean", and the environment is `lib/useDayDock`'s problem.
 *
 * `null` is a browser with no usable storage. Reading the PROPERTY can throw before any method is
 * called (Safari's private mode, a browser set to block site data, an embedded context), and the
 * caller that catches that hands `null` rather than a store that will throw on every touch.
 *
 * ── Every read answers `panel`, whatever it finds ───────────────────────────────────────────
 * CLAUDE.md's rule on browser-persisted data: a record written by an older build is read
 * defensively, never assumed to carry today's shape and never allowed to throw. Three things can
 * go wrong and all three land on the panel — a value this build does not recognise (a future host,
 * a hand-edited entry, a half-written string), a store whose own methods throw, and the absence of
 * the key, which is the ordinary first visit.
 *
 * `panel` rather than the last thing that parsed, because the panel is the host that keeps the week
 * on screen: a trainer who lands in an unexplained full-screen modal has lost the canvas they were
 * reading, and a trainer who lands in the panel has lost a preference they can restate with one
 * press.
 */

/**
 * Which box the day form stands in. It is the `?dock=` value on the wire of the URL, so its two
 * spellings are load-bearing: `full` is the modal, and it keeps that name from the fullscreen host
 * it replaced so an old link and a restored session still open something.
 *
 * It lives HERE rather than beside the search parser because `model/` may not import from `lib/`,
 * and this module is the one that decides what the value MEANS.
 */

/**
 * `movebuddy.` prefixed, as every other preference this app stores is, so one glance at a
 * trainer's storage says which entries are ours.
 */
const KEY = 'movebuddy.dayDock';

/** The host a day opens in when nothing has been chosen, and the answer to anything unreadable. */
const FALLBACK: DayDockMode = 'panel';

export function readDockDefault(store: PreferenceStore | null): DayDockMode {
  if (store === null) return FALLBACK;
  try {
    const stored = store.getItem(KEY);
    return stored === 'panel' || stored === 'full' ? stored : FALLBACK;
  } catch {
    // Reading storage can THROW rather than answer null, and a preference is never worth taking the
    // builder down for. Same shape as `resilientSessionStorage`.
    return FALLBACK;
  }
}

export function writeDockDefault(store: PreferenceStore | null, mode: DayDockMode): void {
  if (store === null) return;
  try {
    store.setItem(KEY, mode);
  } catch {
    // A full or refused storage loses the preference and nothing else. The host the trainer just
    // switched to is in the URL either way, so the act they took still happened.
  }
}
